import Dropzone from '@deltablot/dropzone';
import { mapState } from 'vuex';

Dropzone.autoDiscover = false;

function initVideoUploaderComponent(app) {
    app.component('video-uploader', {
        template: '#video-uploader',
        props: {
            id: { required: true },
            uploadUrl: { required: true },
            thumbnailUploadUrl: { required: true },
            commitChunkVideoUrl: { required: true },
            maxSize: { required: true },
            filesExtension: { required: true },
            uploadButtonText: { default: '' },
            fileSizeErrorText: { default: '' },
            invalidFileTypeText: { default: '' },
            serverSideErrorText: { default: '' },
            cancellUploadText: { default: '' },
            firefoxUnsupportedError: { default: '' },
            maxDuration: { default: 600 },
            thumbnailHeight: { default: 195 },
            thumbnailWidth: { default: 260 },
            chunkSize: { required: true },
        },
        data() {
            return {
                uploader: {},
                inProgress: false,
                showErrorMessage: false,
                errorMessageText: '',
                thumbnailBlob: null,
                exceptionsList: [this.fileSizeErrorText, this.invalidFileTypeText],
                abortController: null,
            };
        },
        watch: {
            uploadingCancelled(val) {
                this.cancelUploading();
            },
        },
        mounted() {
            this.initUploader();
        },
        computed: {
            ...mapState({
                uploadingCancelled: (state) => state.videoUploading.uploadingCancelled
            })
        },
        methods: {
            initUploader() {
                const instance = this;

                this.uploader = new Dropzone(`#${this.id}`, {
                    url: this.uploadUrl,
                    acceptedFiles: this.filesExtension,
                    maxFilesize: this.maxSize,
                    addRemoveLinks: true,
                    autoProcessQueue: true,
                    waitReady: false,
                    uploadMultiple: false,
                    timeout: Number.MAX_VALUE,
                    dictFileTooBig: this.fileSizeErrorText,
                    dictInvalidFileType: this.invalidFileTypeText,
                    dictCancelUpload: this.cancellUploadText,
                    dictResponseError: this.serverSideErrorText,
                    previewsContainer: "#dropzone-previews",
                    maxFiles: 1,
                    chunking: true,
                    parallelChunkUploads: true,
                    retryChunks: true,
                    retryChunksLimit: 3,
                    parallelUploads: 3,
                    chunkSize: Number(this.chunkSize),

                    error(file, errorMessage) {
                        let errorMessageToShow = errorMessage;

                        if (this.files.includes(file) || this.files.length === 0) {
                            // if uploading fails errorMessage contains not short error message but html
                            if (!instance.exceptionsList.includes(errorMessage)) {
                                if (instance.tryHandlerSpecificError(file)) {
                                    return;
                                }

                                errorMessageToShow = instance.serverSideErrorText;
                                instance.$emit('failed');
                            }

                            instance.handleValidationError(errorMessageToShow, file);
                        }
                    },

                    // reused default code from dropzone documentation to override cancelling logic, but left everything else
                    addedfile(file) {
                        if (this.files.length > 1 || instance.inProgress) {
                            this.removeFile(file);
                            return;
                        }

                        instance.showErrorMessage = false;

                        if (this.element === this.previewsContainer) {
                            this.element.classList.add("dz-started");
                        }

                        if (this.previewsContainer && !this.options.disablePreviews) {
                            createPreviewElement(file, this.options, this.previewsContainer);
                            updatePreviewContent(file, this.filesize(file.size));
                            addRemoveLink(file, this.options);
                            attachRemoveEvent(file, instance);
                        }
                    },

                    accept(file, done) {
                        const targetHeight = instance.thumbnailHeight;
                        const targetWidth = instance.thumbnailWidth;

                        const video = document.createElement("video");
                        video.src = URL.createObjectURL(file);

                        video.onloadedmetadata = () => {
                            if (!validateVideoLength(video, instance.maxDuration)) {
                                this.emit("error", file, instance.fileSizeErrorText);
                                return;
                            }

                            const { originalWidth, originalHeight } = calculateDimensions(video);

                            generateResizedPreview(video, originalWidth, originalHeight, targetWidth, targetHeight, file,
                                (dataUrl) => handlePreviewSuccess(dataUrl, file, instance, done),
                                () => this.emit("error", file, instance.serverSideErrorText),
                            );
                        };

                        video.onerror = () => {
                            this.emit("error", file, instance.serverSideErrorText);
                        };
                    },

                    complete(file){
                          attachRemoveEvent(file, instance);
                    },

                    success: async function (file, response) {
                        const dzInstance = this;

                        const onUpload = async (videoId) => {
                            try {
                                dzInstance.removeFile(file);
                                const thumbnailResponse = await instance.uploadThumbnail(videoId);
                                instance.$emit('uploaded', { video: thumbnailResponse });
                            } catch (error) {
                                this.emit("error", file, instance.serverSideErrorText);
                            }
                        };
                    
                        await new Promise(resolve => setTimeout(resolve, 100));

                        let videoId;

                        if (file.upload.chunked) {
                            try{
                                videoId = await instance.commitChunkUpload(file.name, file.upload.totalChunkCount, file.upload.uuid);
                            }catch (error) {
                                if (error.name === "AbortError"){
                                    instance.inProgress = false;
                                    instance.$emit("cancel");
                                    return;
                                }

                                this.emit("error", file, instance.serverSideErrorText);
                                return;
                            }
                        }
                        else {
                            videoId = response;
                        }

                        await onUpload(videoId);
                        instance.inProgress = false;
                    },

                    canceled: () => {
                        instance.inProgress = false;
                        instance.$emit("cancel");
                    },
                });
            },
            cancelUploading() {
                this.uploader.removeAllFiles(true);

                if (this.abortController) {
                    this.abortController.abort();
                }
            },
            async commitChunkUpload(fileName, dztotalchunkcount, dzuuid) {
                const uploadUrl = this.commitChunkVideoUrl;

                this.abortController = new AbortController();
                const signal = this.abortController.signal;

                try {
                    const response = await fetch(uploadUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            fileName: fileName,
                            totalChunkCount: dztotalchunkcount,
                            uuid: dzuuid,
                        }),
                        signal
                    });

                    const data = await response.json();
                    return data;
                } catch (error) {
                    throw error;
                }
            },
            async uploadThumbnail(videoId) {
                if (!this.thumbnailBlob) {
                    throw new Error("No videoBlob available for thumbnail upload");
                }

                const formData = new FormData();
                formData.append('file', this.thumbnailBlob);

                const uploadUrl = `${this.thumbnailUploadUrl}/${videoId}`;

                try {
                    const response = await fetch(uploadUrl, {
                        method: 'POST',
                        body: formData,
                    });

                    const data = await response.json();
                    return data;
                } catch (error) {
                    throw error;
                }
            },
            tryHandlerSpecificError(file) {
                const firefoxBrowserName = "Firefox";
                const matroshkaMimeType = "video/x-matroska";

                const isBrowserFirefox = navigator.userAgent.indexOf(firefoxBrowserName) != -1;
                const isVideoMatroshka = file.type === matroshkaMimeType;

                if (isBrowserFirefox && isVideoMatroshka) {
                    this.handleValidationError(this.firefoxUnsupportedError, file);
                    return true;
                }

                return false;
            },
            handleValidationError(messageToShow, file) {
                this.errorMessageText = messageToShow;
                this.showErrorMessage = true;
                this.inProgress = false;
                this.uploader.removeFile(file);
            }
        },
    });
}

function handlePreviewSuccess(dataUrl, file, instance, done) {
    instance.thumbnailBlob = dataUrlToBlob(dataUrl);
    updatePreview(file, dataUrl);

    instance.inProgress = true;
    instance.$emit('upload-started', file.name);

    done();
}

function validateVideoLength(video, maxDuration) {
    return video.duration <= maxDuration;
}

function calculateDimensions(video) {
    const originalWidth = video.videoWidth;
    const originalHeight = video.videoHeight;

    return { originalWidth, originalHeight };
}

function generateResizedPreview(video, originalWidth, originalHeight, targetWidth, targetHeight, file, callback, errorCallback) {
    const timeupdate = function () {
        try {
            const canvas = snapImage(video);

            video.removeEventListener('timeupdate', timeupdate);
            video.pause();

            const resizedCanvas = createCanvas(targetWidth, targetHeight);
            const resizedContext = resizedCanvas.getContext("2d");

            fillBackground(resizedContext, targetWidth, targetHeight);

            const { drawWidth, drawHeight, offsetX, offsetY } =
                calculateAspectFitDimensions(originalWidth, originalHeight, targetWidth, targetHeight);

            drawResizedImage(canvas, resizedContext, originalWidth, originalHeight, offsetX, offsetY, drawWidth, drawHeight);

            const dataUrl = resizedCanvas.toDataURL();

            handleDataUrl(file, video, dataUrl, callback, errorCallback);
        } catch {
            handleError(video, errorCallback);
        }
    };

    video.onerror = () => handleError(video, errorCallback);

    video.addEventListener('timeupdate', timeupdate);
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.play();
}

function snapImage(video) {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas;
};

function fillBackground(context, targetWidth, targetHeight) {
    context.fillStyle = "black";
    context.fillRect(0, 0, targetWidth, targetHeight);
}

function calculateAspectFitDimensions(originalWidth, originalHeight, targetWidth, targetHeight) {
    const originalAspect = originalWidth / originalHeight;
    const targetAspect = targetWidth / targetHeight;

    let drawWidth, drawHeight, offsetX, offsetY;

    if (originalAspect > targetAspect) {
        drawWidth = targetWidth;
        drawHeight = targetWidth / originalAspect;
        offsetX = 0;
        offsetY = (targetHeight - drawHeight) / 2;
    } else {
        drawWidth = targetHeight * originalAspect;
        drawHeight = targetHeight;
        offsetX = (targetWidth - drawWidth) / 2;
        offsetY = 0;
    }

    return { drawWidth, drawHeight, offsetX, offsetY };
}

function drawResizedImage(canvas, context, originalWidth, originalHeight, offsetX, offsetY, drawWidth, drawHeight) {
    context.drawImage(canvas, 0, 0, originalWidth, originalHeight, offsetX, offsetY, drawWidth, drawHeight);
}

function handleDataUrl(file, video, dataUrl, callback, errorCallback) {
    if (!dataUrl || dataUrl.indexOf("data:") !== 0) {
        return;
    }

    updatePreview(file, dataUrl);
    URL.revokeObjectURL(video.src);
    callback(dataUrl);
}

function handleError(video, errorCallback) {
    URL.revokeObjectURL(video.src);
    errorCallback();
}

function createCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

function updatePreview(file, dataUrl) {
    if (file.previewElement) {
        const img = file.previewElement.querySelector('.dz-image img');
        if (img) {
            img.src = dataUrl;
        }
    }
}

function dataUrlToBlob(dataUrl) {
    if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.includes(",")) {
        return null;
    }

    const [header, base64Data] = dataUrl.split(",");
    const mimeMatch = header.match(/:(.*?);/);
    if (!mimeMatch) {
        return null;
    }

    const mimeString = mimeMatch[1];
    const byteString = atob(base64Data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
}

function createPreviewElement(file, options, previewsContainer) {
    file.previewElement = Dropzone.createElement(options.previewTemplate.trim());
    file.previewTemplate = file.previewElement;
    previewsContainer.appendChild(file.previewElement);
}

function updatePreviewContent(file, sizeHtml) {
    for (const nameNode of file.previewElement.querySelectorAll("[data-dz-name]")) {
        nameNode.textContent = file.name;
    }

    for (const sizeNode of file.previewElement.querySelectorAll("[data-dz-size]")) {
        sizeNode.innerHTML = sizeHtml;
    }
}

function addRemoveLink(file, options) {
    if (options.addRemoveLinks) {
        const removeLinkHTML = `<a class="dz-remove" href="javascript:undefined;" data-dz-remove>${options.dictRemoveFile}</a>`;
        file._removeLink = Dropzone.createElement(removeLinkHTML);
        file.previewElement.appendChild(file._removeLink);
    }
}

function attachRemoveEvent(file, uploaderInstance) {
    const removeFileEvent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploaderInstance.$emit('try-cancel');
    };

    for (const removeLink of file.previewElement.querySelectorAll("[data-dz-remove]")) {
        removeLink.addEventListener("click", removeFileEvent);
    }
}

export default initVideoUploaderComponent;
